<script setup lang="ts">
/**
 * PAGE CAGNOTTE — carte de don de la future page cagnotte (Karim 2026-10-12),
 * volontairement LÉGÈRE : progression (anneau), puis le CHOIX DU MONTANT
 * directement dans la page — mêmes briques que /c/<slug>/payment (boutons +
 * champ libre, « Ce que permet votre don », ligne fiscale discrète). Pas de moyen de
 * paiement ici : « Faire un don » ouvre la page de paiement de la cagnotte
 * dans un nouvel onglet, montant pré-rempli (?montant=).
 */
import DonateAmountSelector from "~/components/donate/DonateAmountSelector.vue";
import type { ImpactOption } from "~/components/donate/DonateImpactOptions.vue";
import DonateFiscalLigne from "~/components/donate/DonateFiscalLigne.vue";
import DonateImpactCarrousel from "~/components/donate/DonateImpactCarrousel.vue";
import type { CagnotteAffichee } from "./types";
import type { EtatDonCagnotte } from "./PageCagnotteEtatDon.vue";
import { MONTANTS_RAPIDES_CAGNOTTE, montantRapideParDefaut } from "./montantsRapides";

const props = defineProps<{
  cagnotte: CagnotteAffichee;
  /** Association réellement habilitée aux reçus fiscaux (ou simulée). */
  fiscal: boolean;
  /** Montants proposés et montant par défaut : ceux de la page de paiement. */
  montants: number[];
  indexDefaut: number;
  /** Paliers « Ce que permet votre don » de la cagnotte. */
  impacts: ImpactOption[];
  couleur?: string | null;
  styleBouton?: string | null;
  /**
   * Date limite (useCampaignPublic) : la barre horizontale lui est RÉSERVÉE
   * (CampaignCountdown de la page actuelle). La progression est un anneau.
   */
  closesAt?: string | null;
  endDateLabel?: string | null;
  enPause?: boolean;
  terminee?: boolean;
  /**
   * Dons fermés : terminée / en pause / brouillon (useCampaignPublic). Le
   * bloc d'état REMPLACE le choix du montant et « Faire un don » — ils
   * restaient affichés sur une cagnotte terminée (Karim 2026-10-15).
   */
  etatDon?: EtatDonCagnotte | null;
  /** Jour de fin en clair, pour une cagnotte terminée. */
  dateFin?: string | null;
  /** Réglage « Afficher le montant collecté » de la cagnotte (faux : ni montant ni anneau). */
  afficherMontant?: boolean;
}>();
const emit = defineEmits<{ partager: []; terminee: [] }>();

const { t } = useI18n();
const { formatCurrency } = useFormatLangue();

const devise = computed(() => props.cagnotte.currency || "EUR");
const objectif = computed(() => Number(props.cagnotte.goal_amount) || 0);
const collecte = computed(() => Number(props.cagnotte.raised) || 0);
const pourcent = computed(() =>
  objectif.value > 0 ? Math.round((collecte.value / objectif.value) * 100) : 0,
);
const montant = (n: number) => formatCurrency(n, { currency: devise.value, decimals: 0 });

// ─── Couleur d'accent = celle des boutons rapides de montant ─────────────
// DonateAmountSelector : couleur de la page de paiement de l'asso si elle en
// a choisi une, sinon dégradé Tailwind blue-600 → blue-500 (mêmes variables
// que ses classes). L'anneau, la barre du temps restant et « Faire un don »
// suivent (Karim 2026-10-12).
const BLEU_FONCE = "var(--color-main2)";
const BLEU_CLAIR = "var(--color-main)";
const accentPlein = computed(() => props.couleur || "var(--color-main)");
const styleBoutonDon = computed(() => ({
  background: props.couleur || "var(--color-main)",
}));
/** Centimes visibles quand il y en a (6,80 €), comme sur les pages de paiement. */
const montantPrecis = (n: number) =>
  formatCurrency(n, { currency: devise.value, decimals: Number.isInteger(n) ? 0 : 2 });

// ─── Anneau de progression (repris de l'ancienne carte : 70 px, trait 9) ──
const ANNEAU = 70;
const TRAIT = 9;
const RAYON = (ANNEAU - TRAIT) / 2;
const CIRCONFERENCE = 2 * Math.PI * RAYON;
/** Part remplie, animée de 0 à la valeur au montage. */
const remplissage = ref(0);
const decalage = computed(() => CIRCONFERENCE - (Math.min(100, remplissage.value) / 100) * CIRCONFERENCE);
onMounted(() => requestAnimationFrame(() => (remplissage.value = pourcent.value)));
watch(pourcent, (v) => (remplissage.value = v));


// ─── Choix du montant ───────────────────────────────────────────────────
/** Toujours 20 / 50 / 100 / 200 €, sur une ligne (montantsRapides.ts). */
const montantsProposes = [...MONTANTS_RAPIDES_CAGNOTTE];
const montantChoisi = ref(String(montantRapideParDefaut(props.montants, props.indexDefaut)));
const valeur = computed(() => Number.parseFloat(montantChoisi.value) || 0);

const touche = ref(false);
/** Mêmes règles que le formulaire de paiement (montant requis, minimum 1 €). */
const erreur = computed(() => {
  if (!montantChoisi.value || Number.isNaN(Number.parseFloat(montantChoisi.value))) {
    return t("don.validationForm.montantRequis");
  }
  if (valeur.value <= 0) return t("don.validationForm.montantPositif");
  if (valeur.value < 1) return t("don.validationForm.montantMinimum");
  return null;
});
const lienPaiement = computed(
  () => `/c/${props.cagnotte.slug}/payment?montant=${encodeURIComponent(String(Math.round(valeur.value * 100) / 100))}`,
);
function verifier(e: MouseEvent) {
  touche.value = true;
  if (erreur.value) e.preventDefault();
}
</script>

<template>
  <div class="rounded-[28px] bg-white shadow-[0_24px_64px_-20px_rgba(12,48,151,0.35)] ring-1 ring-black/[0.06] overflow-hidden">
    <!-- En-tête : où en est la cagnotte -->
    <div class="relative px-6 pt-5 pb-4 bg-white">
      <div class="flex items-start justify-between gap-3">
        <div v-if="afficherMontant !== false" class="flex items-center gap-4 min-w-0">
          <!-- Anneau de progression -->
          <div v-if="objectif > 0" class="relative shrink-0">
            <svg :width="ANNEAU" :height="ANNEAU" class="-rotate-90" aria-hidden="true">
              <!-- Couleur des boutons rapides de montant (accent), piste #e6eefb. -->
              <defs>
                <linearGradient id="page-cagnotte-anneau" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" :style="{ stopColor: BLEU_FONCE }" />
                  <stop offset="100%" :style="{ stopColor: BLEU_CLAIR }" />
                </linearGradient>
              </defs>
              <circle :cx="ANNEAU / 2" :cy="ANNEAU / 2" :r="RAYON" stroke="#e6eefb" :stroke-width="TRAIT" fill="none" />
              <circle
                :cx="ANNEAU / 2"
                :cy="ANNEAU / 2"
                :r="RAYON"
                :stroke="couleur ? couleur : 'url(#page-cagnotte-anneau)'"
                :stroke-width="TRAIT"
                fill="none"
                stroke-linecap="round"
                :stroke-dasharray="CIRCONFERENCE"
                :stroke-dashoffset="decalage"
                class="transition-[stroke-dashoffset] duration-[1200ms] ease-out"
              />
            </svg>
            <span class="absolute inset-0 flex items-center justify-center text-sm text-(--color-ink)" style="font-weight: 800 !important">
              {{ pourcent }}%
            </span>
          </div>
          <div class="min-w-0">
            <!-- 800 = graisse maximale chargée de la police : un léger contour de la
                 même couleur l'épaissit encore un peu (Karim 2026-10-12). -->
            <p class="text-[28px] leading-none text-(--color-ink)" style="font-weight: 800 !important; letter-spacing: -0.02em; -webkit-text-stroke: 0.6px currentColor">
              {{ montant(collecte) }}
            </p>
            <p class="mt-1.5 text-sm text-gray-500">
              {{ objectif > 0 ? $t("campagne.pageCagnotte.collectesSur", { objectif: montant(objectif) }) : $t("campagne.pageCagnotte.collectes") }}
            </p>
          </div>
        </div>
        <span v-else />
        <button
          type="button"
          class="shrink-0 w-10 h-10 rounded-full bg-white ring-1 ring-gray-200 text-(--color-ink) flex items-center justify-center hover:ring-(--color-ink) transition-all"
          :aria-label="$t('campagne.pageCagnotte.partager')"
          :title="$t('campagne.pageCagnotte.partager')"
          @click="emit('partager')"
        >
          <UIcon name="i-lucide-share-2" class="size-4" />
        </button>
      </div>

      <!-- Barre horizontale = la DATE LIMITE (composant de la page actuelle) -->
      <CampaignCountdown
        v-if="closesAt && endDateLabel && !terminee && !enPause"
        :style="{ '--tp-fin': accentPlein }"
        class="mt-4"
        :closes-at="closesAt"
        :starts-at="cagnotte.date_created ?? null"
        :end-date-label="endDateLabel"
        :days-remaining="cagnotte.days_remaining ?? null"
        @ended="emit('terminee')"
      />
    </div>

    <!-- Choix du montant (mêmes briques que la page de paiement).
         Dons fermés (Karim 2026-10-15) : le bloc reste tel quel, VOILÉ (flou +
         gris, inactif), et l'état s'affiche par-dessus. -->
    <div class="relative border-t border-gray-100">
    <div
      class="px-6 pt-2 pb-6 space-y-5"
      :class="etatDon ? 'contenu-voile' : ''"
      :inert="!!etatDon"
      :aria-hidden="!!etatDon || undefined"
    >
      <div class="flex items-center justify-between pt-4">
        <p class="text-[15px] text-(--color-ink)" style="font-weight: 700 !important">{{ $t("campagne.pageCagnotte.votreDon") }}</p>
        <span class="inline-flex items-center gap-1 text-xs text-gray-500">
          <UIcon name="i-lucide-lock" class="size-3.5" /> {{ $t("campagne.pageCagnotte.paiementSecurise") }}
        </span>
      </div>

      <DonateAmountSelector
        v-model:amount="montantChoisi"
        :suggested-amounts="montantsProposes"
        :format-amount="montantPrecis"
        :has-error="touche && !!erreur"
        :error-message="erreur"
        :primary-color="couleur"
        :button-style="styleBouton"
        :colonnes="4"
        champ-libre
      />

      <!-- « Ce que permet votre don » : carrousel commun avec le formulaire
           de don intégrable (DonateImpactCarrousel). -->
      <DonateImpactCarrousel
        v-model:amount="montantChoisi"
        :impacts="impacts"
        :format-amount="montantPrecis"
        :primary-color="couleur"
      />

      <!-- Réduction d'impôt en une ligne discrète (pas l'encart noir des pages
           de paiement), calcul sur le montant choisi -->
      <DonateFiscalLigne v-if="fiscal" :final-amount="erreur ? 0 : valeur" :format-amount="montantPrecis" />

      <a
        :href="lienPaiement"
        target="_blank"
        rel="noopener"
        class="flex items-center justify-center gap-2 w-full py-3.5 rounded-full text-white text-[16px] no-underline transition-[filter] duration-200 hover:brightness-95"
        :style="styleBoutonDon"
        :class="erreur ? 'opacity-60' : ''"
        style="font-weight: 700 !important; color: white !important"
        @click="verifier"
      >
        <UIcon name="i-lucide-heart" class="size-5" />
        {{ $t("campagne.pageCagnotte.faireUnDon") }}
      </a>

      <p class="flex items-center justify-center gap-1.5 text-xs text-gray-500">
        <UIcon name="i-lucide-shield-check" class="size-4 shrink-0 text-(--color-main2)" />
        {{ $t("campagne.pageCagnotte.paiementSecuriseDetail") }}
      </p>
    </div>
    <PageCagnotteEtatDon
      v-if="etatDon"
      :etat="etatDon"
      :date-fin="dateFin"
      :slug-association="cagnotte.organization_slug"
    />
    </div>
  </div>
</template>

<style scoped>
/* Dons fermés : le contenu reste visible, flouté et grisé, sous l'état. */
.contenu-voile {
  filter: blur(5px) grayscale(1);
  opacity: 0.75;
  user-select: none;
}
</style>
